import { http } from '@/api/http';

/**
 * Self-service profile endpoint (PTV2-1107).
 *
 * BE contract (PTV2-1108, option A — merged 2026-06-11):
 *   PUT `/api/v1/users/{uuid}` accepts a partial `UpdateUserRequest`. For
 *   non-admin (self) callers, only `first_name`, `last_name`, `title` are
 *   accepted; sending `email`, `role`, `name`, `fullName`, `status`,
 *   `tenantID`, or `indexNumber` is rejected with
 *   `422 VALIDATION_FAILED` and a `sourcePointer` naming the offending field
 *   (the BE treats it as a privilege-escalation attempt).
 *
 *   Self-edit semantics:
 *     - The caller's `{uuid}` must equal their own GUID — otherwise 404.
 *     - Partial update: omitted field = unchanged. Explicit `null` on
 *       `title` clears it.
 *     - `email` is OIDC-linked → admin-only.
 *     - `role` is the system permission enum → admin-only, managed via
 *       role-assignment endpoints.
 *     - `title` is the free-text display role shown on Care Team rows.
 */
export interface UpdateUserRequestBody {
  readonly first_name?: string;
  readonly last_name?: string;
  readonly title?: string | null;
}

/**
 * Raw transport surface for `PUT /users/{uuid}` driven by the profile screen.
 * Kept feature-local (rather than under `admin-users/endpoints.ts`) because
 * the self-service path is conceptually distinct from the admin directory.
 */
export const userProfileEndpoints = {
  updateUser: (
    uuid: string,
    body: UpdateUserRequestBody,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.put<undefined>(`/users/${encodeURIComponent(uuid)}`, body, options),
};
